import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {PersistedExtractionJob} from "@/types";
import Spinner from "@/Components/Spinner";
import PrimaryButton from "@/Components/PrimaryButton";
import LanguageWidget from "@/Pages/Dashboard/Translate/LanguageWidget";
import {googleTranslate} from "@/services/translate/google/GoogleTranslateService";
import {translate as aiTranslate} from "@/services/translate/AI/AiTranslateService";
import toast from "react-hot-toast";
import {AnalyticsEvent, sendEvent} from "@/services/analytics";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faRightLeft} from "@fortawesome/free-solid-svg-icons";
import {TranslationMethod} from "@/types/enums";
import Popover from "@/Components/Popover";
import Select from "@/Components/Select";
import AiTranslateInfoModal from "@/Pages/Dashboard/Translate/AiTranslateInfoModal";
import {formatMinutes, getLanguageName} from "@/utils/textFormatting";
import GoogleIcon from "@/Components/Icons/GoogleIcon";
import AiIcon from "@/Components/Icons/AiIcon";
import ProgressBar from "@/Components/ProgressBar";
import InfoIcon from "@/Components/Icons/InfoIcon";
import PurchaseModal from "@/Pages/Dashboard/Purchase/PurchaseModal";
import InsufficientCreditsException from "@/exceptions/InsufficientCreditsException";
import {MultiViewModal} from "@/Components/MultiViewModal";
import {getSubtitleData} from "@/services/subtitleService";
import {useConfirm, useIsConfirmOpen} from "@/Components/ConfirmDialog";
import TranslationResultsModal from "@/Pages/Dashboard/Translate/TranslationResultsModal";
import {TranslationSummary} from "@/types";
import {Subtitle} from "@/models/Subtitle";

type CommonProps = {
    show: boolean;
    onClose: (inProgressLanguages?: string[]) => void;
    onTranslateComplete: (summary: TranslationSummary, method: TranslationMethod) => void;
    isFreeTrial: boolean;
};

type BulkProps = CommonProps & {
    bulk: true;
    jobs: PersistedExtractionJob[];
};

type SingleProps = CommonProps & {
    bulk: false;
    job: PersistedExtractionJob;
    currentLanguage: string;
    translatedLanguages: string[];
    subtitles: Subtitle[];
};

export type TranslateModalProps = BulkProps | SingleProps;

type Store = {
    selectedLanguages: string[];
    method: TranslationMethod;
}

const STORE_KEY = 'translate_modal';

export default function TranslateModal(props: TranslateModalProps) {
    const {show, onClose, onTranslateComplete, isFreeTrial, bulk} = props;
    const jobs = bulk ? props.jobs : [props.job];
    const currentLanguage = bulk ? null : props.currentLanguage;
    const translatedLanguages = bulk ? [] : props.translatedLanguages;
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([])
    const [isTranslating, setIsTranslating] = useState(false);
    const [method, setMethod] = useState<TranslationMethod>(TranslationMethod.GOOGLE);
    const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
    const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
    const [creditBalance, setCreditBalance] = useState(0);
    const [progress, setProgress] = useState(0);
    const abortControllerRef = useRef(new AbortController());
    const [currentView, setCurrentView] = useState(0);
    const [translationResults, setTranslationResults] = useState<TranslationSummary | null>(null);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [sourceLanguage, setSourceLanguage] = useState<string | null>(null);

    const availableSourceLanguages = useMemo(() => {
        if (bulk) {
            return [];
        }
        const languages = new Set(translatedLanguages);
        languages.add(currentLanguage!);
        return Array.from(languages);
    }, [bulk, currentLanguage, translatedLanguages]);

    const languageNames = useMemo(() =>
        selectedLanguages.map(lang => getLanguageName(lang)).join(', '),
        [selectedLanguages]
    );

    const [jobSubtitles, setJobSubtitles] = useState<Record<string, Subtitle[]>>({});
    const [highlightLanguagePicker, setHighlightLanguagePicker] = useState(false);
    const translationRate = laravelConfig<number>('pricing.credit_rates.translation', 0.5);
    const creditsNeeded = jobs.reduce(
        (acc: number, job: PersistedExtractionJob) => acc + Math.ceil(job.file_duration / 60 * translationRate), 0
    ) * selectedLanguages.length;
    const confirm = useConfirm()
    const isConfirming = useIsConfirmOpen();
    const isSubtitlesLoaded = Object.keys(jobSubtitles).length === jobs.length;

    const startTranslation = useCallback(
        runTranslate,
        [selectedLanguages, method, jobSubtitles, sourceLanguage]
    )

    useEffect(() => {
        const prevState = localStorage.getItem(STORE_KEY);
        if (prevState) {
            const {selectedLanguages: prevLang, method: prevMethod} = JSON.parse(prevState) as Store;
            setSelectedLanguages(prevLang);
            setMethod(prevMethod);
        }
    }, []);

    useEffect(() => {
        const state: Store = {selectedLanguages, method};
        localStorage.setItem(STORE_KEY, JSON.stringify(state));
    }, [selectedLanguages, method]);

    useEffect(() => {
        if (selectedLanguages.length) {
            setHighlightLanguagePicker(false);
        }
    }, [selectedLanguages]);

    useEffect(() => {
        if(!show) {
            setHighlightLanguagePicker(false);
            abortControllerRef.current.abort();
            setSourceLanguage(null)
            setProgress(0)
        } else {
            abortControllerRef.current = new AbortController();
        }
    }, [show]);

    const loadSubtitles = async (language: string | null) => {
        const subtitles = await Promise.all(
            jobs.map(job => getSubtitleData(job.id, language || '').then(res => res.subtitles))
        );
        const subtitlesWithIds = jobs.map((job, index) => [job.id, subtitles[index]]);
        setJobSubtitles(Object.fromEntries(subtitlesWithIds));
    }

    useEffect(() => {
        if (show) {
            setSourceLanguage(translatedLanguages[0] || null);
        } else {
            setJobSubtitles({});
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            if (bulk) {
                loadSubtitles(sourceLanguage).catch(() => toast.error('Failed to load subtitles'))
            }
        } else {
            setTimeout(() => {
                setCurrentView(0)
                setIsTranslating(false);
            }, 250);
        }
    }, [show, bulk]);

    useEffect(() => {
        if (!bulk && show && sourceLanguage) {
            if(sourceLanguage === currentLanguage) {
                setJobSubtitles({[jobs[0].id.toString()]: props.subtitles})
            } else {
                loadSubtitles(sourceLanguage).catch(() => toast.error('Failed to load subtitles'));
            }
        }
    }, [bulk, show, sourceLanguage]);

    useEffect(() => {
        if (isTranslating) {
            sendEvent(AnalyticsEvent.TRANSLATE, {
                language_code: selectedLanguages.join(', '),
                language_name: languageNames,
                method
            });
        }
    }, [isTranslating]);

    useEffect(() => {
        if (showMoreInfoModal) {
            sendEvent(AnalyticsEvent.VIEW_AI_TRANSLATE_INFO, {
                language_name: languageNames,
            });
        }
    }, [showMoreInfoModal]);

    async function handleClose() {
        const canSkipConfirm = !isTranslating || method === TranslationMethod.AI && !bulk;
        if (
            !isConfirming &&
            (canSkipConfirm || await confirm({ message: 'Are you sure you want to exit?'}))
        ) {
            setIsTranslating(false);
            setTimeout(() => setCurrentView(0), 250);
            onClose(
                isTranslating &&
                method === TranslationMethod.AI ? selectedLanguages : undefined
            );
        }
    }

    async function runTranslate() {
        if(!selectedLanguages.length) {
            setHighlightLanguagePicker(true);
            return;
        }

        if (
            sourceLanguage && selectedLanguages.includes(sourceLanguage) ||
            availableSourceLanguages.length === 1 && selectedLanguages.includes(availableSourceLanguages[0])
        ) {
            const languageName = getLanguageName(sourceLanguage || availableSourceLanguages[0]);
            toast.error(`Cannot translate from ${languageName} to ${languageName}`);
            return;
        }

        const alreadyTranslated = selectedLanguages.filter(lang => translatedLanguages.includes(lang) || lang === currentLanguage);
        if (alreadyTranslated.length) {
            if (!await confirm({
                message: 'You have already translated to ' + alreadyTranslated.map(l => getLanguageName(l)).join(', ') +
                    '. Do you want to overwrite the existing translations?',
                title: 'Overwrite Translation'
            })) {
                return;
            }
        }

        setIsTranslating(true);
        const abortSignal = abortControllerRef.current.signal;
        const onProgress = (val: number) => !abortSignal.aborted && setProgress(val);

        setCurrentView(method === TranslationMethod.GOOGLE ? 1 : 2);

        try {
            let summary: TranslationSummary;

            if (method === TranslationMethod.GOOGLE) {
                summary = await googleTranslate(
                    jobs,
                    jobSubtitles,
                    selectedLanguages,
                    sourceLanguage,
                    onProgress,
                    abortSignal
                );
            } else {
                summary = await aiTranslate(
                    jobs,
                    selectedLanguages,
                    sourceLanguage,
                    onProgress
                );
            }

            if(abortSignal.aborted) {
                return;
            }

            if (summary.hasSuccesses && summary.hasFailures) {
                setTranslationResults(summary);
                setShowResultsModal(true);
            } else {
                if (summary.hasSuccesses) {
                    const total = summary.results.reduce((sum, res) => sum + res.successful.length, 0);
                    toast.success(total > 1 ? 'All translations completed successfully' : 'Translation completed successfully');
                } else {
                    const total = summary.results.reduce((sum, res) => sum + res.failed.length, 0);
                    toast.error(total > 1 ? 'All translations failed' : 'Translation failed');
                }
                onTranslateComplete(summary, method);
            }

        } catch (error) {
            if (method === TranslationMethod.GOOGLE) {
                setCurrentView(0);
            } else {
                setCurrentView(1);
                if (error instanceof InsufficientCreditsException) {
                    setCreditBalance(error.currentBalance);
                    setShowCreditPurchaseModal(true);
                } else {
                    toast.error(error instanceof Error ? error.message : 'Translation failed');
                }
            }
        }

        setIsTranslating(false);
        setProgress(0);
    }

    const MethodSelectionView = useMemo(() => (
        <div className="px-7" data-title="Select Translation Method">
            <label id="aria-label" htmlFor="lang-input" className="text-sm text-gray-700 dark:text-gray-300">Translation Method</label>
            <div className="flex w-full mt-2 pb-2 md:pb-7">
                <button type="button"
                        onClick={() => setMethod(TranslationMethod.GOOGLE)}
                        className={`px-4 grow py-5 cursor-pointer group text-sm font-medium flex flex-col items-center flex-1
                    border-2 rounded  hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 ${method === TranslationMethod.GOOGLE ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'}`}>
                    <GoogleIcon className="h-5 w-5 mb-1.5"/>
                    <span className="font-bold text-md mb-0.5">Google Translate</span>
                    <span
                        className="bg-gray-100 dark:bg-gray-700 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-medium mt-1 px-2.5 py-0.5 rounded">Free</span>
                </button>
                <div className="w-3"></div>
                <Popover
                    delay={0}
                    trigger="hover"
                    placement="top"
                    aria-labelledby="default-popover"
                    content={
                        <div className="w-72 text-sm text-gray-500 dark:text-gray-400">
                            <div className="px-3 py-2">
                                <p>Get more accurate and natural sounding translations using AI.</p>
                                <span
                                    onClick={() => setShowMoreInfoModal(true)}
                                    className="text-blue-600 dark:text-blue-400 inline-block mt-2 font-bold cursor-pointer hover:underline">More Info</span>
                            </div>
                        </div>
                    }
                >
                    <button type="button"
                            onClick={() => setMethod(TranslationMethod.AI)}
                            className={`px-4 grow py-5 text-sm font-medium flex flex-col items-center flex-1 cursor-pointer
                        border-2 rounded  hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400 ${method === TranslationMethod.AI ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'}`}>
                        <AiIcon className="h-5 w-5 text-orange-400 mb-1.5"/>
                        <span className="font-bold text-md mb-0.5">Translate with AI</span>
                        <span className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 text-xs font-medium mt-1  px-2.5 py-0.5 rounded">Superior Quality</span>
                    </button>
                </Popover>
            </div>
            {!bulk && availableSourceLanguages.length > 1 && (
                <div className="pb-4 w-full">
                    <label className="text-sm mb-2 inline-block text-gray-700 dark:text-gray-300">Source Language</label>
                    <Select<{ value: string; label: string; isOriginal: boolean }>
                        variant="modal"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        isSearchable={false}
                        options={availableSourceLanguages.map(lang => ({
                            value: lang,
                            label: getLanguageName(lang),
                            isOriginal: lang === translatedLanguages[0],
                        }))}
                        value={sourceLanguage ? {
                            value: sourceLanguage,
                            label: getLanguageName(sourceLanguage),
                            isOriginal: sourceLanguage === translatedLanguages[0],
                        } : null}
                        onChange={(option) => setSourceLanguage(option?.value || null)}
                        formatOptionLabel={(option) => (
                            <span className="flex items-center justify-between flex-1">
                                <span>{option.label}</span>
                                {option.isOriginal && (
                                    <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-medium px-2 py-0.5 rounded ml-2">Original</span>
                                )}
                            </span>
                        )}
                    />
                </div>
            )}
            <div className=" grow google-element w-full">
                <label id="aria-label" htmlFor="lang-input" className="text-sm mb-2 inline-block text-gray-700 dark:text-gray-300">Translation Languages</label>
                <LanguageWidget
                    multiple={true}
                    highlight={highlightLanguagePicker}
                    onSelect={setSelectedLanguages}
                    selectedLanguage={selectedLanguages}
                    method={method}
                />
            </div>

            <div className="pt-4 md:pt-7  w-full ">
                {method === TranslationMethod.GOOGLE ? (
                    <PrimaryButton
                        disabled={isTranslating || !isSubtitlesLoaded}
                        className={'w-full mb-4'}
                        onClick={startTranslation}
                    >
                        <FontAwesomeIcon
                            className="text-white  w-3 h-3 pr-2 align-middle "
                            icon={faRightLeft}/>
                        <span>Translate</span> {isTranslating &&
                        <Spinner className="ml-2 align-middle inline-block"/>}
                    </PrimaryButton>
                ) : (
                    <Popover arrowClassName="!bg-gray-50" placement="top" trigger="hover" disabled={!isFreeTrial}
                             content={<FreeTrialPopover/>}>
                        <div>
                            <PrimaryButton
                                disabled={!isSubtitlesLoaded || isFreeTrial}
                                className={'w-full mb-4'}
                                onClick={() => selectedLanguages.length ? setCurrentView(1) : setHighlightLanguagePicker(true)}
                            >
                                <span>Continue</span>
                                <FontAwesomeIcon className="text-white w-3 h-3 pl-2 align-middle" icon={faArrowRight}/>
                            </PrimaryButton>
                        </div>
                    </Popover>
                )}
            </div>
        </div>
    ), [
        selectedLanguages,
        isTranslating,
        method,
        isSubtitlesLoaded,
        showMoreInfoModal,
        creditBalance,
        highlightLanguagePicker,
        availableSourceLanguages,
        sourceLanguage,
        startTranslation
    ]);

    const ConfirmationView = useMemo(() => (
        <div className="flex flex-col h-80">
            <div className="flex-grow px-7 flex flex-col">
                <div className=" h-full justify-center flex-col flex mb-6">
                    <div className="border-b border-gray-100 dark:border-gray-700 text-sm pb-3 mb-3 px-1 sm:px-3 flex justify-between items-center">
                        <div className="mb-1  ">
                            <div className="flex items-center">
                                <span className="text-gray-500 dark:text-gray-400 text-base">Credits Used</span>
                                <Popover
                                    delay={0}
                                    trigger="hover"
                                    placement="bottom"
                                    content={
                                        <div className="w-72 text-sm text-gray-500 dark:text-gray-400 p-4">
                                            <p>Each minute of video translation costs {translationRate} processing minute{translationRate !== 1 ? 's' : ''}. For
                                                example,
                                                translating a 60-minute video uses {Math.round(60 * translationRate)} credits.</p>
                                        </div>
                                    }
                                >
                                    <div><InfoIcon
                                        className="text-gray-300 dark:text-gray-600 ml-1 cursor-pointer hover:text-blue-700 dark:hover:text-blue-400 !w-5 !h-5"/>
                                    </div>
                                </Popover>
                            </div>
                        </div>
                        <span
                            className="font-bold text-gray-800 dark:text-gray-200">{formatMinutes(creditsNeeded)}</span>
                    </div>
                    {(sourceLanguage && sourceLanguage !== 'und') && (
                        <div className="border-b border-gray-100 dark:border-gray-700 pb-3 mb-3 px-1 sm:px-3 text-sm flex justify-between items-center">
                            <span className="text-gray-500 dark:text-gray-400 mb-1 block text-base">Source Language</span>
                            <span className="font-bold text-gray-800 dark:text-gray-200">{getLanguageName(sourceLanguage)}</span>
                        </div>
                    )}
                    <div className="border-b border-gray-100 dark:border-gray-700 pb-3 mb-3 px-1 sm:px-3 text-sm flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400 mb-1 block text-base">Target Language</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{languageNames}</span>
                    </div>
                    <div className="px-1 sm:px-3 text-sm flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400 mb-1 block text-base "><span className="hidden sm:inline">Estimated </span><span>Processing Time</span></span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{`${jobs.length > 1 ? '1-5' : '1-3'}`} Minutes</span>
                    </div>
                </div>
                <div className=" mb-4">
                    <PrimaryButton
                        disabled={!selectedLanguages.length || !isSubtitlesLoaded}
                        className={'w-full flex items-center justify-center'}
                        onClick={startTranslation}
                    >
                        <FontAwesomeIcon className="text-white w-3 h-3 pr-2" icon={faRightLeft}/>
                        <span>Translate</span>
                    </PrimaryButton>
                </div>
            </div>
        </div>
    ), [selectedLanguages, languageNames, isSubtitlesLoaded, sourceLanguage, creditsNeeded, startTranslation]);

    const ProgressView = useMemo(() => (
        <div className="flex flex-col h-80">
            <div className="flex-grow px-7 flex flex-col">
                <div className=" h-full justify-center flex-col flex">
                    <p className="text-center mb-4 font-bold text-gray-800 dark:text-gray-200">Translation in progress</p>
                    <ProgressBar max={100} progress={progress} height="md" color="blue"/>
                    <span
                        className="text-gray-500 dark:text-gray-400 mt-3 text-center"><span className="notranslate">{progress.toFixed()}%</span> <span>Complete</span></span>
                </div>
                <div className=" mb-4">
                    {method === TranslationMethod.AI && !bulk ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Closing this window will not cancel the translation</p>
                    ) : (
                        <PrimaryButton
                            disabled={true}
                            className={'w-full flex items-center justify-center'}
                        >
                            <FontAwesomeIcon className="text-white w-3 h-3 pr-2" icon={faRightLeft}/>
                            <span>Translate</span>
                            <Spinner className="ml-2"/>
                        </PrimaryButton>
                    )}
                </div>
            </div>
        </div>
    ), [progress, method, bulk]);

    const handleResultsModalClose = () => {
        setShowResultsModal(false);
        if (translationResults) {
            onTranslateComplete(translationResults, method);
        }
        setTranslationResults(null);
    };


    return (
        <>
            <TranslationResultsModal
                show={showResultsModal}
                onClose={handleResultsModalClose}
                summary={translationResults}
            />
            <MultiViewModal
                currentIdx={currentView}
                onBack={() => setCurrentView(0)}
                showBackButton={!isTranslating}
                title="Translate Subtitles"
                maxWidth={'lg'}
                show={show} onClose={handleClose}
                extras={
                    <>
                        <AiTranslateInfoModal show={showMoreInfoModal} onClose={() => setShowMoreInfoModal(false)}/>
                        <PurchaseModal
                            show={showCreditPurchaseModal}
                            onCompleted={async () => {
                                setShowCreditPurchaseModal(false);
                                await startTranslation();
                            }}
                            message={`You have ${formatMinutes(creditBalance)} of processing credits remaining, but need ${formatMinutes(creditsNeeded)} to run this translation.`}
                            onClose={() => setShowCreditPurchaseModal(false)}
                        />
                    </>
                }
            >
                {...(method === TranslationMethod.AI
                    ? [MethodSelectionView, ConfirmationView, ProgressView]
                    : [MethodSelectionView, ProgressView]
                )}
            </MultiViewModal>
        </>
    );
}

function FreeTrialPopover() {
    return (
        <div className="px-4 pt-2 w-72 bg-gray-50 dark:bg-gray-800">
            <span className="inline-block mb-3 text-sm text-gray-700 dark:text-gray-300">
                AI translation requires a <a className="text-blue-700 dark:text-blue-400 hover:underline" href={route('pricing')} target="_blank">paid account</a>. Purchase credits to unlock this feature.
            </span>
        </div>
    )
}
